import React, { useState, useEffect } from 'react';
import { TC, TOverlay } from './trainTheme';
const BASE_URL = 'https://scores.aprmode.com';

const fmt = (d) => d ? new Date(d + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '';
const gradeColor = (g) => ({ A: '#10b981', B: '#3b82f6', C: '#f59e0b', D: '#f87171' }[g] || TC.muted);

export function ACSLogModal({ item, onClose, onSave, token, navigate }) {
  const todayStr = new Date().toISOString().slice(0, 10);
  const [date, setDate]             = useState(todayStr);
  const [notes, setNotes]           = useState('');
  const [ctx, setCtx]               = useState(null);   // { flights, cfis, schools }
  const [ctxLoading, setCtxLoading] = useState(true);
  const [flightId, setFlightId]     = useState('');     // scored_approach_id string
  const [verifierType, setVerifierType] = useState(''); // 'cfi' | 'school' | ''
  const [verifierId, setVerifierId]     = useState(''); // id string

  const freq = item.frequency_days || 90;
  const freqLabel = freq >= 365 ? `${freq/365} year${freq/365 > 1 ? 's' : ''}` : `${freq} days`;
  const daysUntil = Math.ceil(freq - (Date.now() - new Date(date)) / 86400000);
  const dueDate   = new Date(new Date(date).getTime() + freq * 86400000)
                      .toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });

  useEffect(() => {
    if (!token) { setCtxLoading(false); return; }
    fetch('https://scores.aprmode.com/api/train/acs/log_context', { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json())
      .then(d => { setCtx(d); setCtxLoading(false); })
      .catch(() => setCtxLoading(false));
  }, [token]);

  const hasFlight   = ctx && ctx.flights && ctx.flights.length > 0;
  const hasVerifier = ctx && (ctx.cfis.length > 0 || ctx.schools.length > 0);

  // Combine CFIs and schools into one verifier list
  const verifierOptions = ctx ? [
    ...ctx.cfis.map(c => ({ value: `cfi:${c.id}`, label: `CFI — ${c.display_name || c.username}` })),
    ...ctx.schools.map(s => ({ value: `school:${s.id}`, label: `School — ${s.name}` })),
  ] : [];

  const handleSave = () => {
    const [vType, vId] = verifierId ? verifierId.split(':') : [null, null];
    onSave(item.id, notes, date, {
      scored_approach_id: flightId ? parseInt(flightId) : null,
      verifier_type: vType || null,
      verifier_id: vId ? parseInt(vId) : null,
    });
  };

  const selectedFlight = hasFlight && flightId
    ? ctx.flights.find(f => String(f.id) === flightId)
    : null;

  const inputStyle = {
    width: '100%', padding: '9px 12px', borderRadius: 8,
    border: `1px solid ${TC.border}`, background: TC.bg, color: TC.text,
    fontSize: 15, boxSizing: 'border-box',
  };

  return (
    <TOverlay onClose={onClose}>
      <div style={{ fontSize: 17, fontWeight: 700, color: TC.text, marginBottom: 2 }}>Log Practice</div>
      <div style={{ fontSize: 14, color: TC.muted, marginBottom: 14, lineHeight: 1.4 }}>{item.task}</div>

      {/* Date */}
      <div style={{ marginBottom: 12 }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: TC.dim, textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 6 }}>Date practiced</div>
        <input type="date" value={date} max={todayStr}
          onChange={e => setDate(e.target.value)} style={inputStyle} />
      </div>

      {/* Next due info */}
      <div style={{ padding: '9px 12px', borderRadius: 8, background: TC.bg, border: `1px solid ${TC.border}`, marginBottom: 12 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
          <span style={{ fontSize: 13, color: TC.muted }}>Recommended every</span>
          <span style={{ fontSize: 13, fontWeight: 700, color: TC.text }}>{freqLabel}</span>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 4 }}>
          <span style={{ fontSize: 13, color: TC.muted }}>Next due</span>
          <span style={{ fontSize: 13, fontWeight: 700, color: daysUntil <= 0 ? TC.red : daysUntil <= 30 ? TC.amber : TC.green }}>
            {dueDate} {daysUntil > 0 ? `(${daysUntil}d)` : '(overdue)'}
          </span>
        </div>
      </div>

      {/* Link flight */}
      <div style={{ marginBottom: 12 }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: TC.dim, textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 6 }}>
          Link a flight <span style={{ color: TC.muted, fontWeight: 400, textTransform: 'none' }}>(optional)</span>
        </div>
        {ctxLoading ? (
          <div style={{ fontSize: 13, color: TC.muted, padding: '8px 0' }}>Loading flights…</div>
        ) : hasFlight ? (
          <select value={flightId} onChange={e => setFlightId(e.target.value)} style={{ ...inputStyle, cursor: 'pointer' }}>
            <option value="">— No flight linked —</option>
            {ctx.flights.map(f => (
              <option key={f.id} value={String(f.id)}>
                {f.callsign} → {f.arr_airport}{f.runway_id ? ' RWY ' + f.runway_id : ''} · {fmt(f.flight_date)} · {f.grade != null && f.percentage != null ? f.grade + '/' + f.percentage + '%' : 'NS'}{f.source === 'FA' ? ' VFR' : ''}
              </option>
            ))}
          </select>
        ) : (
          <div style={{ padding: '10px 12px', borderRadius: 8, background: TC.bg, border: `1px solid ${TC.border}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <span style={{ fontSize: 13, color: TC.muted }}>No claimed flights in the last 90 days</span>
            {navigate && (
              <button onClick={() => { onClose(); navigate('/flights'); }}
                style={{ background: 'none', border: 'none', color: TC.accent, fontSize: 13, fontWeight: 700, cursor: 'pointer', padding: 0, whiteSpace: 'nowrap', marginLeft: 10 }}>
                → Go to Flights
              </button>
            )}
          </div>
        )}
        {selectedFlight && (
          <div style={{ marginTop: 6, padding: '7px 10px', borderRadius: 7, background: 'rgba(59,130,246,0.08)', border: '1px solid rgba(59,130,246,0.2)', display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 18, fontWeight: 800, color: gradeColor(selectedFlight.grade) }}>{selectedFlight.grade}</span>
            <span style={{ fontSize: 13, color: TC.muted }}>{selectedFlight.callsign} → {selectedFlight.arr_airport} · {selectedFlight.percentage != null ? selectedFlight.percentage + '%' : 'NS'}</span>
          </div>
        )}
      </div>

      {/* Send to CFI / School */}
      <div style={{ marginBottom: 14 }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: TC.dim, textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 6 }}>
          Send for verification <span style={{ color: TC.muted, fontWeight: 400, textTransform: 'none' }}>(optional)</span>
        </div>
        {ctxLoading ? (
          <div style={{ fontSize: 13, color: TC.muted, padding: '8px 0' }}>Loading…</div>
        ) : hasVerifier ? (
          <select value={verifierId} onChange={e => setVerifierId(e.target.value)} style={{ ...inputStyle, cursor: 'pointer' }}>
            <option value="">— No verification —</option>
            {verifierOptions.map(v => <option key={v.value} value={v.value}>{v.label}</option>)}
          </select>
        ) : (
          <div style={{ padding: '10px 12px', borderRadius: 8, background: TC.bg, border: `1px solid ${TC.border}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <span style={{ fontSize: 13, color: TC.muted }}>No linked CFI or school</span>
            {navigate && (
              <button onClick={() => { onClose(); navigate('/settings#training-team'); }}
                style={{ background: 'none', border: 'none', color: TC.accent, fontSize: 13, fontWeight: 700, cursor: 'pointer', padding: 0, whiteSpace: 'nowrap', marginLeft: 10 }}>
                → Link in Settings
              </button>
            )}
          </div>
        )}
      </div>

      {/* Notes */}
      <textarea value={notes} onChange={e => setNotes(e.target.value)}
        placeholder="Optional notes (conditions, aircraft, instructor...)"
        rows={2}
        style={{ ...inputStyle, resize: 'none', marginBottom: 14 }} />

      <div style={{ display: 'flex', gap: 8 }}>
        <button onClick={onClose}
          style={{ flex: 1, padding: '10px 0', borderRadius: 8, border: `1px solid ${TC.border}`,
            background: TC.bg, color: TC.muted, fontSize: 15, fontWeight: 600, cursor: 'pointer' }}>Cancel</button>
        <button onClick={handleSave}
          style={{ flex: 2, padding: '10px 0', borderRadius: 8, border: 'none',
            background: TC.accent, color: '#fff', fontSize: 15, fontWeight: 700, cursor: 'pointer' }}>
          ✓ Log Practice
        </button>
      </div>
    </TOverlay>
  );
}

export default ACSLogModal;
